import { useEffect, useMemo, useState } from "react";
import { listConsents } from "../api/endpoints";
import type { Consent } from "../api/types";
import { useI18n } from "../i18n";

export default function ConsentsPage() {
  const { t } = useI18n();

  const [items, setItems] = useState<Consent[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string | undefined>();
  const [error, setError] = useState<string | null>(null);

  const statusLabel = useMemo(() => {
    return (status: string) => {
      const key = `consents.status.${String(status || "").toUpperCase()}`;
      const v = t(key);
      return v === key ? status : v;
    };
  }, [t]);

  async function load(filter?: string) {
    setLoading(true);
    try {
      const res = await listConsents(filter);
      setItems(res.items || []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || t("consents.errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(statusFilter);
  }, [statusFilter]);

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">{t("consents.title")}</div>
          <div className="page-subtitle">{t("consents.subtitle")}</div>
        </div>

        <select
          className="form-select"
          value={statusFilter || ""}
          onChange={(e) => setStatusFilter(e.target.value || undefined)}
        >
          <option value="">{t("consents.filters.all")}</option>
          <option value="PENDING">{t("consents.filters.pending")}</option>
          <option value="GRANTED">{t("consents.filters.granted")}</option>
          <option value="REVOKED">{t("consents.filters.revoked")}</option>
        </select>
      </div>

      <div className="card">
        {loading && <p className="text-muted small">{t("common.loading")}</p>}
        {error && <p className="form-error">{error}</p>}

        {!loading && !items.length && !error && (
          <p className="text-muted small">{t("consents.empty")}</p>
        )}

        {!!items.length && (
          <table className="table">
            <thead>
              <tr>
                <th>{t("consents.table.quest")}</th>
                <th>{t("consents.table.status")}</th>
                <th>{t("consents.table.created")}</th>
                <th>{t("consents.table.updated")}</th>
                <th>{t("consents.table.notes")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td>{c.questId}</td>
                  <td>{statusLabel(String(c.status))}</td>
                  <td>
                    {new Date(c.createdAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "2-digit"
                    })}
                  </td>
                  <td>
                    {new Date(c.updatedAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "2-digit"
                    })}
                  </td>
                  <td>{c.notes || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
